import React, { useState } from "react"
import {
    useForm,
    StringField,
    EmailField,
    PhoneNumberField,
    ListField,
    NumberField,
    MappingField,
} from "@alexanderathoodly/formify"
import { ThemeProvider } from "@material-ui/core/styles"
import {
    MUITheme,
    EmbedForm,
    MUISelectField,
    MUITextInputField,
    BodyText,
    PageSection,
} from "@alexanderathoodly/ui-library"
import { GAClientID } from "../../analytics/init"
import { api } from "../../helpers/api"
import { FormLeadPayload } from "@alexanderathoodly/data-models"
import { FieldDefinition } from "@alexanderathoodly/formify"

const SIZE_SEGMENTS: { [key: string]: { min: number; max: number | null } } = {
    "1-2": { min: 1, max: 2 },
    "3-5": { min: 3, max: 5 },
    "6-10": { min: 6, max: 10 },
    "11-20": { min: 11, max: 20 },
    "21-50": { min: 21, max: 50 },
    "50+": { min: 50, max: null },
}

const SIZE_SEGMENT_CHOICES = Object.keys(SIZE_SEGMENTS).map((v) => ({
    display: v,
    value: v,
}))

const SIZE_SEGMENTS_MAPPING: {
    [key: string]: { min: number; max: number | null } | null
} = { "": null, ...SIZE_SEGMENTS }

const AREA_CHOICES = [
    "Innanför tullarna",
    "Stockholm City",
    "Vasastan",
    "Östermalm",
    "Södermalm",
    "Kungsholmen",
    "Kista",
    "Sundbyberg",
    "Solna",
    "Övrigt",
].map((s) => ({ display: s, value: s }))

function isRestrictedDomain(value: string): string {
    const restrictedDomains = [
        "gmail.com",
        "hotmail.com",
        "outlook.com",
        "live.se",
        "yahoo.com",
        "icloud.com",
        "hotmail.se",
        "yahoo.se",
    ]
    const position = value.indexOf("@")
    if (position) {
        const domain = value.substring(position + 1)
        return restrictedDomains.indexOf(domain.toLowerCase()) < 0 ? "" : "Please enter company email"
    }
    return ""
}

function extractErrorMessage(
    e: Error & { statusCode: number; responseJson?: Record<string, any> },
    form: ReturnType<typeof useForm>
) {
    const errorMessages: string[] = []
    switch (e.statusCode) {
        case 400: {
            if (!e.responseJson) break
            const content = e.responseJson
            form.setErrors(content)
            if (content.non_field_errors) {
                errorMessages.push(content.non_field_errors)
            } else if (content.details) {
                for (const [key, errors] of Object.entries(form.state.errors)) {
                    if (errors.length > 0) {
                        errorMessages.push(`${key}: ${errors.join(", ")}`)
                    }
                }
            }
            break
        }
        default:
            errorMessages.push(e.toString())
    }
    return errorMessages.join(", ")
}
const useInterestForm = (): {
    form: ReturnType<typeof useForm>
    isSending: boolean
    error: string
    isValid: boolean
} => {
    const [isSending, setIsSending] = useState(false)
    const [error, setError] = useState("")

    const fields: FieldDefinition = {
        size_segment: MappingField<{ min: number | null; max: number | null } | null>(SIZE_SEGMENTS_MAPPING, {
            defaultValue: null,
            validators: [
                (v: { min: number | null; max: number | null } | null): string =>
                    v === null ? "A size segment is required" : "",
            ],
        }),
        areas: ListField(StringField(), { minLength: 1 }),
        monthly_budget: NumberField({ required: true, minValue: 0 }),
        company: StringField({ required: true }),
        email: EmailField({ required: true, validators: [isRestrictedDomain] }),
        phone_number: PhoneNumberField({ required: true }),
    }

    const handleFormSubmit = async (data: Omit<FormLeadPayload, "submitted" | "ga_client_id">): Promise<void> => {
        setIsSending(true)
        try {
            //send lead form
            const payload = {
                submitted: true,
                ga_client_id: GAClientID || "",
                ...data,
            }
            await api.createFormLead(payload)
            // add email to url if meeting booking is allowed
            const query = payload.size_segment.max && payload.size_segment.max > 2 ? `?email=${payload.email}` : ""
            // the confirmation path is not in the gatsby router
            // use full page reset to navigate to the confirmation
            window.location.href = `/kontorshjalpen/success${query}`
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error)
            setError(extractErrorMessage(error, form as ReturnType<typeof useForm>))
        } finally {
            setIsSending(false)
        }
    }
    const form = useForm({
        fields,
        onFormSubmit: handleFormSubmit,
    })
    return { form: form as ReturnType<typeof useForm>, isSending, error, isValid: form.isFormValid() }
}

export interface LeadFormSectionProps {
    template: "lead-form"
    color: "light-blue" | "white"
    id?: string
}

const LeadForm: React.FC<LeadFormSectionProps> = ({ id, color }) => {
    const { form, isSending, error, isValid } = useInterestForm()

    return (
        <ThemeProvider theme={MUITheme}>
            <PageSection backgroundColor={color} id={id || undefined}>
                <EmbedForm
                    className="medium-width-content"
                    headline="Hitta rätt kontorshotell"
                    buttonTitle="Kontakta mig"
                    buttonDisabled={!isValid || isSending}
                    error={error}
                    onSubmit={() => {
                        if (!isValid || isSending) return
                        form.trySubmit()
                    }}
                    footer={
                        <BodyText>
                            I vår databas samlar vi alla tillgängliga och kommande rum på kontorshotell. Vi går igenom
                            hela marknadens utbud och kurerar en lista med lämpliga lösningar utifrån era behov.
                        </BodyText>
                    }
                >
                    <MUISelectField
                        label="Antal anställda"
                        name="size_segment"
                        options={SIZE_SEGMENT_CHOICES}
                        form={form}
                    />
                    <MUISelectField label="Plats" name="areas" options={AREA_CHOICES} form={form} multiple />
                    <MUITextInputField label="Företag" name="company" form={form} />
                    <MUITextInputField label="Budget" name="monthly_budget" form={form} />
                    <MUITextInputField label="Telefon" name="phone_number" form={form} />
                    <MUITextInputField label="E-post" name="email" form={form} />
                </EmbedForm>
            </PageSection>
        </ThemeProvider>
    )
}

export { LeadForm }
